import { message } from "@norn/widget-renderer/messages"
import init, { checkArtifactSet, evaluateExpression, initSync } from "@norn/checker-wasm"
import { parseExact, stringifyExact } from "./lossless"
import { asArray, asString, isRecord } from "./utils"

/** One `norn-checker/diagnostics@1` finding. */
export interface Diagnostic {
  readonly code: string
  readonly severity: string
  readonly message: string
  readonly pointer: string | undefined
  readonly artifactId: string | undefined
}

export interface CheckOutcome {
  readonly admitted: boolean
  readonly diagnostics: readonly Diagnostic[]
}

function toDiagnostic(d: Record<string, unknown>): Diagnostic {
  return {
    code: asString(d.code) ?? "?",
    severity: asString(d.severity) ?? "error",
    message: asString(d.message) ?? "",
    pointer: isRecord(d.span) ? asString(d.span.pointer) : asString(d.path),
    artifactId: asString(d.artifactId) ?? asString(d.baseArtifactId),
  }
}

/** Findings from a `norn-checker/diagnostics@1` document (preflight output). */
export function diagnosticsOf(doc: unknown): Diagnostic[] {
  return isRecord(doc) ? asArray(doc.diagnostics).filter(isRecord).map(toDiagnostic) : []
}

export interface InstallRefusalEvidence {
  readonly diagnostics: readonly Diagnostic[]
  /** Narrowing findings: changes the cell will only accept with an explicit narrowing review. */
  readonly narrowing: readonly string[]
  readonly error: string | undefined
}

/**
 * The evidence a PUT /v1/defs refusal carries, read structurally: checker refusals carry a
 * diagnostics document, simple refusals a diagnostics array, narrowing refusals a narrowing list,
 * other refusals an error string, and a generic Refusal (or a 409) only its common fields.
 */
export function installRefusalEvidence(body: unknown): InstallRefusalEvidence {
  if (!isRecord(body)) return { diagnostics: [], narrowing: [], error: undefined }
  const raw = body.diagnostics
  const list = Array.isArray(raw) ? raw : isRecord(raw) && Array.isArray(raw.diagnostics) ? raw.diagnostics : []
  const narrowing = Array.isArray(body.narrowing)
    ? body.narrowing.filter(isRecord).map((n) => [asString(n.artifactId), asString(n.kind), asString(n.detail)].filter(Boolean).join(" · "))
    : []
  return { diagnostics: list.filter(isRecord).map(toDiagnostic), narrowing, error: asString(body.error) }
}

let ready: Promise<unknown> | null = null

/** Instantiate from bytes already in hand (Node tests) instead of fetching the .wasm. */
export function initCheckerSync(bytes: BufferSource): void {
  initSync({ module: bytes })
  ready = Promise.resolve()
}

/**
 * Check a candidate layer against the registry base with the Rust checker
 * compiled to WASM: the same admission the cell runs, without a round trip.
 * The cell remains authoritative; install re-checks.
 */
export async function preflight(candidateJson: string, baseJson: string): Promise<CheckOutcome> {
  ready ??= init()
  await ready
  const out: unknown = JSON.parse(checkArtifactSet(candidateJson, `{"base":${baseJson}}`))
  return { admitted: isRecord(out) && out.admitted === true, diagnostics: isRecord(out) ? diagnosticsOf(out.diagnostics) : [] }
}


/** The typed outcome of one Norn expression evaluation (`norn-zen/6`, `norn.limits/1` fuel). */
export type EvaluationResult = { readonly ok: true; readonly value: unknown } | { readonly ok: false; readonly code: string; readonly message: string }

/** Evaluate an admitted WidgetIR expression over its explicit inputs (record/draft/selection/context). */
export function evaluate(expression: string, inputs: Readonly<Record<string, unknown>>, locale = "en"): EvaluationResult {
  // Exact numbers go in as their tokens and come back as tokens (lossless on both sides).
  const out = parseExact(evaluateExpression(expression, stringifyExact(inputs), undefined)) as Record<string, unknown>
  if (out.ok === true) return { ok: true, value: out.value }
  const error = isRecord(out.error) ? out.error : {}
  const first = asArray(error.diagnostics).filter(isRecord)[0]
  return { ok: false, code: asString(error.code) ?? "evaluation.failed", message: asString(error.message) ?? asString(first?.message) ?? message(locale, "checker.expressionFailed") }
}

/** Load the WASM checker once, then return the synchronous evaluator. */
export async function loadEvaluator(locale = "en"): Promise<typeof evaluate> {
  ready ??= init()
  await ready
  return (expression, inputs, evaluationLocale = locale) => evaluate(expression, inputs, evaluationLocale)
}
